'use client'

import { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { useOffMarketAuth } from './OffMarketAuthContext'
import { Button } from '@/components/ui/button'
import { inputVariants } from '@/components/ui/input'
import { cn } from '@/utilities/ui'
import { omitContact, reportToLeadCentre } from '@/utilities/leadCentre'

interface OffMarketSignInModalProps {
  open: boolean
  onClose: () => void
}

export function OffMarketSignInModal({ open, onClose }: OffMarketSignInModalProps) {
  const { signIn } = useOffMarketAuth()
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  // Asana 003 — the "Sign Up" link used to navigate away; the register form now
  // lives in this modal as a second mode. Successful registration signs the new
  // member straight in (same om_session cookie), unlocking the gated listings.
  const [mode, setMode] = useState<'signin' | 'signup'>('signin')
  // Fields mirror the /sign-up page's form (Steve 2026-08-19): First Name,
  // Last Name, Email, Mobile — all full width.
  const [signup, setSignup] = useState({ firstName: '', surname: '', mobile: '' })
  const ALERT_OPTIONS = [
    'Residential Sales',
    'Residential Rentals',
    'Commercial Sales',
    'Commercial Rentals',
  ]
  const [alertPrefs, setAlertPrefs] = useState<string[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  const overlayRef = useRef<HTMLDivElement>(null)

  // Focus input when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 100)
    } else {
      setEmail('')
      setError('')
      setMode('signin')
      setSignup({ firstName: '', surname: '', mobile: '' })
      setAlertPrefs([])
    }
  }, [open])

  // Close on escape
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  // Prevent body scroll
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)

    const result = await signIn(email.trim())

    if (result.success) {
      onClose()
    } else if (result.error === 'not_found') {
      setError('Email not found. Sign up below to get access.')
    } else {
      setError('Something went wrong. Please try again.')
    }

    setSubmitting(false)
  }

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSubmitting(true)
    try {
      const res = await fetch('/api/off-market-members', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          ...signup,
          alertPreferences: alertPrefs.join(', '),
        }),
      })
      if (res.ok) {
        reportToLeadCentre({
          name: [signup.firstName, signup.surname].filter(Boolean).join(' '),
          email: email.trim(),
          phone: signup.mobile,
          leadType: 'OFF_MARKET',
          sourceForm: 'off-market-signup',
          fields: {
            ...omitContact(signup, ['firstName', 'surname', 'mobile']),
            alert_preferences: alertPrefs,
          },
        })

        const result = await signIn(email.trim())
        if (result.success) onClose()
        else setError('Registered! Please sign in with your email.')
      } else {
        const json = await res.json().catch(() => null)
        const duplicate = JSON.stringify(json || '').includes('unique')
        setError(
          duplicate
            ? 'That email is already registered — sign in instead.'
            : 'Something went wrong. Please try again.',
        )
      }
    } catch {
      setError('Something went wrong. Please try again.')
    }
    setSubmitting(false)
  }

  const signupField = (
    label: string,
    key: keyof typeof signup,
    placeholder: string,
    required = false,
  ) => (
    <div>
      <label className="block text-body-xs uppercase font-light text-foreground mb-2">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </label>
      <input
        type="text"
        required={required}
        value={signup[key]}
        onChange={(e) => setSignup((prev) => ({ ...prev, [key]: e.target.value }))}
        placeholder={placeholder}
        className={cn(inputVariants({ className: 'w-full' }))}
      />
    </div>
  )

  if (!open) return null

  return (
    <div
      ref={overlayRef}
      className="bg-background/80 fixed inset-0 z-50 flex items-center justify-center p-0 backdrop-blur-lg sm:p-4"
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose()
      }}
    >
      <div className="bg-background relative flex h-full max-h-none w-full max-w-none flex-col overflow-y-auto p-6 sm:mx-4 sm:h-auto sm:max-h-[90vh] sm:w-full sm:max-w-md sm:rounded-sm sm:border sm:border-border sm:shadow-md sm:p-8">
        {/* Close button — canonical Icon Button (Figma 2198:34803), type=ghost size=Small
            (32px square, 16px glyph). Was a bare hand-rolled <button> with #000/40 →
            #000 hex colours. */}
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={onClose}
          className="absolute top-2 right-2"
          aria-label="Close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </Button>

        <h2 className="font-display text-display-lg font-normal text-foreground mb-6">
          {mode === 'signin'
            ? 'Sign In To The Off-Market Register'
            : 'Sign Up To The Off-Market Register'}
        </h2>

        {mode === 'signup' && (
          <form onSubmit={handleSignUp} className="flex flex-col gap-4">
            {signupField('First Name', 'firstName', 'e.g. Alex', true)}
            {signupField('Last Name', 'surname', 'e.g. Smith', true)}
            <div>
              <label className="block text-body-xs uppercase font-light text-foreground mb-2">
                Email<span className="text-destructive"> *</span>
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setError('')
                }}
                placeholder="your@email.com"
                className={cn(inputVariants({ className: 'w-full' }))}
              />
            </div>
            {signupField('Mobile', 'mobile', '04XX XXX XXX')}
            {/* Asana 003 — same "Set alert preferences." group as /sign-up */}
            <div className="flex flex-col gap-3">
              <p className="text-body-md font-normal text-foreground">Set alert preferences.</p>
              <div className="flex flex-col gap-2">
                {ALERT_OPTIONS.map((opt) => (
                  <label
                    key={opt}
                    className="flex cursor-pointer items-center gap-3 text-body-sm font-light text-foreground"
                  >
                    <input
                      type="checkbox"
                      checked={alertPrefs.includes(opt)}
                      onChange={(e) =>
                        setAlertPrefs((prev) =>
                          e.target.checked ? [...prev, opt] : prev.filter((o) => o !== opt),
                        )
                      }
                      className="size-4 shrink-0 cursor-pointer appearance-none rounded-sm border border-border bg-white shadow-xs checked:border-2 checked:border-accent checked:bg-accent focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring"
                    />
                    {opt}
                  </label>
                ))}
              </div>
            </div>
            {error && <p className="text-body-xs text-destructive">{error}</p>}
            <Button type="submit" loading={submitting} className="w-full">
              {submitting ? 'Signing Up…' : 'Sign Up'}
            </Button>
            <div className="text-center">
              <Button asChild variant="link">
                <button
                  type="button"
                  onClick={() => {
                    setMode('signin')
                    setError('')
                  }}
                >
                  Already Registered? Back To Login.
                </button>
              </Button>
            </div>
          </form>
        )}

        {mode === 'signin' && (
          <form onSubmit={handleSubmit}>
            <label className="block text-body-xs uppercase font-light text-foreground mb-2">
              Email
            </label>
            <input
              ref={inputRef}
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value)
                setError('')
              }}
              placeholder="Enter your email"
              required
              aria-invalid={error ? true : undefined}
              // Canonical Input styling (Figma 2198:38512): filled theme/muted field,
              // radius sm 2, shadow-xs, Body/bodySm 14/16 light, theme/muted-foreground
              // placeholder, 3px theme/ring focus. Applied via `inputVariants` rather than
              // <Input> because the canonical component's props are typed from
              // InputHTMLAttributes and so cannot carry the focus `ref` this modal needs.
              className={cn(inputVariants({ className: 'w-full' }))}
            />

            {error && <p className="mt-2 text-body-xs text-destructive">{error}</p>}

            <Button type="submit" loading={submitting} className="mt-6 w-full">
              {submitting ? 'Signing In…' : 'Sign In'}
            </Button>
          </form>
        )}

        {/* Canonical Link button (Figma 2198:34729) — underlined at rest, zero padding,
            theme/foreground, Body/bodyMdAlt 16/400, and NO hover colour change (Figma
            defines the link button's hover as identical to its rest state). */}
        {mode === 'signin' && (
          <div className="mt-6 text-center">
            {/* Asana 003 — was a page navigation; now switches to the register form. */}
            <Button asChild variant="link">
              <button
                type="button"
                onClick={() => {
                  setMode('signup')
                  setError('')
                }}
              >
                Or Sign Up To The Off-Market Register
              </button>
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
